import { createRoute, createRouter } from "@tanstack/react-router";

import { AccountPage } from "./routes/account";
import { AssistantsPage } from "./routes/assistants";
import { AuditPage } from "./routes/audit";
import { BooksPage } from "./routes/books";
import { CajaPage } from "./routes/caja";
import { CompaniesPage } from "./routes/companies";
import { CompanyDataPage } from "./routes/company-data";
import { ConnectPage } from "./routes/connect";
import { CounterpartsPage } from "./routes/counterparts";
import { ImportPage } from "./routes/import";
import { PeriodsPage } from "./routes/periods";
import { PaymentPlansPage, type PaymentPlansSearch } from "./routes/payment-plans";
import { PurchaseRegisterPage, type PurchaseRegisterSearch } from "./routes/purchase-register";
import { ReceivableAccountPage } from "./routes/receivable-account";
import { ReceivablesPage } from "./routes/receivables";
import { rootRoute } from "./routes/root";
import { StudioPage } from "./routes/studio";
import { TaxAccountsPage } from "./routes/tax-accounts";
import {
  SettlementFormPage,
  SettlementsPage,
  type SettlementFormSearch,
  type SettlementsSearch,
} from "./routes/settlements";
import { VoucherFormPage, type VoucherFormSearch } from "./routes/voucher-form";
import { VouchersPage, type VouchersSearch } from "./routes/vouchers";

const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : undefined);
const dir = (v: unknown) => (v === "compra" || v === "venta" ? v : undefined);
const isoDate = (v: unknown) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
const estado = (v: unknown) => (v === "anulados" || v === "todos" ? v : undefined);

/** Drops absent search params, so `exactOptionalPropertyTypes` holds: absent, never undefined. */
function clean<T extends object>(value: { [K in keyof T]-?: T[K] | undefined }): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: CompaniesPage,
});

const companyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/empresas/$companyId" });

const vouchersRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/comprobantes",
  validateSearch: (s: Record<string, unknown>): VouchersSearch =>
    clean<VouchersSearch>({ periodo: str(s.periodo), dir: dir(s.dir), estado: estado(s.estado) }),
  component: function Vouchers() {
    const { companyId } = vouchersRoute.useParams();
    return <VouchersPage companyId={companyId} search={vouchersRoute.useSearch()} />;
  },
});

const purchaseRegisterRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/registro-compras",
  validateSearch: (s: Record<string, unknown>): PurchaseRegisterSearch =>
    clean<PurchaseRegisterSearch>({
      periodo: str(s.periodo),
      estado: estado(s.estado),
      desde: isoDate(s.desde),
      hasta: isoDate(s.hasta),
    }),
  component: function PurchaseRegister() {
    const { companyId } = purchaseRegisterRoute.useParams();
    return (
      <PurchaseRegisterPage companyId={companyId} search={purchaseRegisterRoute.useSearch()} />
    );
  },
});

const voucherFormRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/comprobantes/nuevo",
  validateSearch: (s: Record<string, unknown>): VoucherFormSearch =>
    clean<VoucherFormSearch>({
      id: str(s.id),
      dir: dir(s.dir),
      periodo: str(s.periodo),
      detalle: s.detalle === true || s.detalle === "1" ? true : undefined,
    }),
  component: function VoucherForm() {
    const { companyId } = voucherFormRoute.useParams();
    const search = voucherFormRoute.useSearch();
    return <VoucherFormPage key={search.id ?? "new"} companyId={companyId} search={search} />;
  },
});

const settlementsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/liquidaciones",
  validateSearch: (s: Record<string, unknown>): SettlementsSearch =>
    clean<SettlementsSearch>({ periodo: str(s.periodo) }),
  component: function Settlements() {
    const { companyId } = settlementsRoute.useParams();
    return <SettlementsPage companyId={companyId} search={settlementsRoute.useSearch()} />;
  },
});

const settlementFormRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/liquidaciones/nueva",
  validateSearch: (s: Record<string, unknown>): SettlementFormSearch =>
    clean<SettlementFormSearch>({ periodo: str(s.periodo), id: str(s.id) }),
  component: function SettlementForm() {
    const { companyId } = settlementFormRoute.useParams();
    const search = settlementFormRoute.useSearch();
    return <SettlementFormPage key={search.id ?? "new"} companyId={companyId} search={search} />;
  },
});

const cajaRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/caja",
  validateSearch: (s: Record<string, unknown>): { periodo?: string } =>
    clean<{ periodo?: string }>({ periodo: str(s.periodo) }),
  component: function Caja() {
    const { companyId } = cajaRoute.useParams();
    return <CajaPage companyId={companyId} search={cajaRoute.useSearch()} />;
  },
});

const periodsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/periodos",
  component: function Periods() {
    const { companyId } = periodsRoute.useParams();
    return <PeriodsPage companyId={companyId} />;
  },
});

const importRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/importar",
  component: function Import() {
    const { companyId } = importRoute.useParams();
    return <ImportPage companyId={companyId} />;
  },
});

const booksRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/libros",
  validateSearch: (s: Record<string, unknown>): { periodo?: string } =>
    clean<{ periodo?: string }>({ periodo: str(s.periodo) }),
  component: function Books() {
    const { companyId } = booksRoute.useParams();
    return <BooksPage companyId={companyId} search={booksRoute.useSearch()} />;
  },
});

const taxAccountsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/cuentas-tributarias",
  component: function TaxAccounts() {
    const { companyId } = taxAccountsRoute.useParams();
    return <TaxAccountsPage key={companyId} companyId={companyId} />;
  },
});

const paymentPlansRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/planes-de-pago",
  validateSearch: (s: Record<string, unknown>): PaymentPlansSearch =>
    clean<PaymentPlansSearch>({
      tab:
        s.tab === "planes" || s.tab === "detalle" || s.tab === "fondos" || s.tab === "historial"
          ? s.tab
          : undefined,
      plan: str(s.plan),
      planes: str(s.planes),
    }),
  component: function PaymentPlans() {
    const { companyId } = paymentPlansRoute.useParams();
    return (
      <PaymentPlansPage
        key={companyId}
        companyId={companyId}
        search={paymentPlansRoute.useSearch()}
      />
    );
  },
});

const companyDataRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/datos",
  component: function CompanyData() {
    const { companyId } = companyDataRoute.useParams();
    return <CompanyDataPage companyId={companyId} />;
  },
});

const counterpartsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contrapartes",
  component: CounterpartsPage,
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auditoria",
  component: AuditPage,
});

const studioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/estudio",
  component: StudioPage,
});

const connectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/conectar",
  component: ConnectPage,
});

const receivablesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cobranzas",
  component: ReceivablesPage,
});

const receivableAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cobranzas/$companyId",
  component: function ReceivableAccount() {
    const { companyId } = receivableAccountRoute.useParams();
    return <ReceivableAccountPage key={companyId} companyId={companyId} />;
  },
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cuenta",
  component: AccountPage,
});

const assistantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/asistentes",
  component: AssistantsPage,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  companyRoute.addChildren([
    vouchersRoute,
    purchaseRegisterRoute,
    voucherFormRoute,
    settlementsRoute,
    settlementFormRoute,
    cajaRoute,
    periodsRoute,
    importRoute,
    booksRoute,
    taxAccountsRoute,
    paymentPlansRoute,
    companyDataRoute,
  ]),
  counterpartsRoute,
  auditRoute,
  studioRoute,
  connectRoute,
  assistantsRoute,
  accountRoute,
  receivablesRoute,
  receivableAccountRoute,
]);

export function createAppRouter() {
  return createRouter({ routeTree });
}

export const router = createAppRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
